import { Filesystem, Directory } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import { isNativePlatform, getPlatform } from './platform';

/**
 * Result type for download operations
 */
export type DownloadResult = {
  success: boolean;
  method: 'direct' | 'share' | 'none';
};

/**
 * Convert a Blob to a base64 string (without data URL prefix)
 */
const blobToBase64 = (blob: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      // Remove data URL prefix (e.g., "data:image/jpeg;base64,")
      const base64 = result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

/**
 * Extract base64 data from a data URL
 */
const extractBase64 = (dataUrl: string): string => {
  const base64Index = dataUrl.indexOf(',');
  return base64Index !== -1 ? dataUrl.substring(base64Index + 1) : dataUrl;
};

/**
 * Download/save an image on Android using direct save to Downloads folder.
 * Falls back to Share sheet if direct save fails.
 * 
 * @param imageUrlOrBase64 - Either an image URL to fetch, or a base64 data URL (starts with "data:")
 * @param fileName - The filename to save as
 * @returns DownloadResult indicating success and method used
 */
export const downloadImageNative = async (
  imageUrlOrBase64: string,
  fileName: string
): Promise<DownloadResult> => {
  // Only use native download on Android
  if (!isNativePlatform() || getPlatform() !== 'android') {
    return { success: false, method: 'none' };
  }

  try {
    let base64Data: string;

    // Check if input is already a base64 data URL (watermarked image)
    if (imageUrlOrBase64.startsWith('data:')) {
      base64Data = extractBase64(imageUrlOrBase64);
    } else {
      // Fetch the image from URL
      const response = await fetch(imageUrlOrBase64, { mode: 'cors' });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch image: ${response.status}`);
      }

      const blob = await response.blob();
      base64Data = await blobToBase64(blob);
    }

    // Try direct save to Downloads folder first (works on Android 11+)
    try {
      await Filesystem.writeFile({
        path: `DecorCanvas/${fileName}`,
        data: base64Data,
        directory: Directory.External,
        recursive: true,
      });
      
      console.log('Direct save to Downloads/DecorCanvas succeeded');
      return { success: true, method: 'direct' };
    } catch (directSaveError) {
      console.log('Direct save failed, falling back to Share sheet:', directSaveError);
      
      // Fall back to Share sheet
      const file = await Filesystem.writeFile({
        path: fileName,
        data: base64Data,
        directory: Directory.Cache,
      });

      await Share.share({
        title: 'Save Image',
        files: [file.uri],
      });

      return { success: true, method: 'share' };
    }
  } catch (error) {
    console.error('Native download failed:', error);
    return { success: false, method: 'none' };
  }
};

/**
 * Result type for batch download operations
 */
export type BatchDownloadResult = {
  count: number;
  method: 'direct' | 'share' | 'none';
};

/**
 * Download/save multiple images on Android.
 * Tries direct save to Downloads folder first, falls back to Share sheet.
 * 
 * @param imageUrlsOrBase64s - Array of image URLs or base64 data URLs
 * @param fileNamePrefix - Prefix for filenames
 * @returns BatchDownloadResult with count and method used
 */
export const downloadAllImagesNative = async (
  imageUrlsOrBase64s: string[],
  fileNamePrefix: string = 'design-variation'
): Promise<BatchDownloadResult> => {
  // Only use native download on Android
  if (!isNativePlatform() || getPlatform() !== 'android') {
    return { count: -1, method: 'none' }; // -1 indicates web fallback should be used
  }

  const preparedImages: { base64: string; fileName: string }[] = [];

  // Prepare all images first
  for (let i = 0; i < imageUrlsOrBase64s.length; i++) {
    try {
      const imageData = imageUrlsOrBase64s[i];
      const fileName = `${fileNamePrefix}-${i + 1}.jpg`;
      let base64Data: string;

      // Check if input is already a base64 data URL
      if (imageData.startsWith('data:')) {
        base64Data = extractBase64(imageData);
      } else {
        // Fetch the image from URL
        const response = await fetch(imageData, { mode: 'cors' });
        
        if (!response.ok) {
          console.error(`Failed to fetch image ${i + 1}: ${response.status}`);
          continue;
        }

        const blob = await response.blob();
        base64Data = await blobToBase64(blob);
      }

      preparedImages.push({ base64: base64Data, fileName });

      // Small delay between fetch operations
      if (i < imageUrlsOrBase64s.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    } catch (error) {
      console.error(`Failed to prepare image ${i + 1}:`, error);
    }
  }

  if (preparedImages.length === 0) {
    return { count: 0, method: 'none' };
  }

  // Try direct save to Downloads folder first
  try {
    let directSaveCount = 0;
    
    for (const img of preparedImages) {
      try {
        await Filesystem.writeFile({
          path: `DecorCanvas/${img.fileName}`,
          data: img.base64,
          directory: Directory.External,
          recursive: true,
        });
        directSaveCount++;
      } catch (writeError) {
        console.error(`Direct save failed for ${img.fileName}:`, writeError);
        throw writeError; // Trigger fallback to Share
      }
    }
    
    console.log(`Direct save to Downloads/DecorCanvas succeeded for ${directSaveCount} images`);
    return { count: directSaveCount, method: 'direct' };
  } catch (directSaveError) {
    console.log('Direct batch save failed, falling back to Share sheet:', directSaveError);
    
    // Fall back to Share sheet
    const fileUris: string[] = [];
    
    for (const img of preparedImages) {
      try {
        const file = await Filesystem.writeFile({
          path: img.fileName,
          data: img.base64,
          directory: Directory.Cache,
        });
        fileUris.push(file.uri);
      } catch (cacheError) {
        console.error(`Cache write failed for ${img.fileName}:`, cacheError);
      }
    }

    if (fileUris.length === 0) {
      return { count: 0, method: 'none' };
    }

    try {
      await Share.share({
        title: 'Save Images',
        files: fileUris,
      });
      return { count: fileUris.length, method: 'share' };
    } catch (shareError) {
      console.error('Share failed:', shareError);
      return { count: 0, method: 'none' };
    }
  }
};
